import { useEffect, useState } from "react"

import { useNavigate, useParams } from "react-router-dom"

import { CollectionItem, ProjectConfiguration, SupportedEmbeddingTypes, VectorSearchQuery } from "@vertesia/common"
import { ArrowLeft, FilterX, Upload } from "lucide-react"
import { toast } from "sonner"

import { ContentDispositionButton } from "@components/buttons/ContentDispositionButton"
import { UploadDocumentsButton } from "@components/buttons/UploadDocumentsButton"
import { DocumentDataTable } from "@components/datatables/DocumentDataTable"
import { FiltersPanel } from "@components/filters/FiltersPanel"
import { DocumentGrid } from "@components/grids/DocumentGrid"

import { LAST_DISPLAYED_VIEW } from "@constants/cookies"

import { transformFacetStatusResponses, transformFacetTypeResponses } from "@lib/facet"

import { FacetResponse } from "@models/facets"

import { useWatchDocumentSearchFacets, useWatchDocumentSearchResult } from "@plugins/providers/document/DocumentSearchContext"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"

interface SearchFilters {
    [key: string]: any
}

export function CollectionSearchPanel() {
    const { cId } = useParams()
    const [collection, setCollection] = useState<CollectionItem | undefined>(undefined)

    const navigate = useNavigate()

    const { hasMore, search, isSearching, error, objects } = useWatchDocumentSearchResult()
    const facets = useWatchDocumentSearchFacets()

    const { client, project, typeRegistry } = useUserSession()

    const [config, setConfig] = useState<ProjectConfiguration | undefined>(undefined)
    const embeddingsReady = !!project && (!!config?.embeddings.text || !!config?.embeddings.image)

    const [isGridView, setIsGridView] = useState(localStorage.getItem(LAST_DISPLAYED_VIEW) === "grid")

    const [filters, setFilters] = useState<SearchFilters>({})
    const [statuses, setStatuses] = useState<FacetResponse[]>([])
    const [types, setTypes] = useState<FacetResponse[]>([])
    const [users, setUsers] = useState<FacetResponse[]>([])

    const [isReady, setIsReady] = useState(true)

    const [clearFilters, setClearFilters] = useState(0)

    const navigateTo404 = () => {
        navigate("/404")
    }

    if (!cId) {
        navigateTo404()
    }

    const handleBackToSearch = () => {
        navigate("/collections")
    }

    const searchCollection = () => {
        if (!client || !cId) {
            return
        }

        client.store.collections
            .retrieve(cId)
            .then((response) => {
                if (response) {
                    setCollection(response)
                } else {
                    navigateTo404()
                }
            })
            .catch(() => {
                navigateTo404()
            })
    }

    const generateTextEmbeddings = async (vectorText: string) => {
        if (!embeddingsReady || !vectorText || !config?.embeddings.text) {
            return
        }

        const response = await client.environments.embeddings(config.embeddings.text?.environment, { model: config.embeddings.text?.model, text: vectorText })

        const query: VectorSearchQuery = {
            values: response.values
        }

        return query
    }

    const searchObjects = async () => {
        const vectorText = filters?.vectorText?.value as string
        const vector: VectorSearchQuery | undefined = (vectorText ? await generateTextEmbeddings(vectorText) : undefined) as VectorSearchQuery

        const query: any = filters
            ? {
                createdFrom: filters?.date?.createdFrom,
                createdTo: filters?.date?.createdTo,
                updatedFrom: filters?.date?.updatedFrom,
                updatedTo: filters?.date?.updatedTo,
                name: filters?.name,
                status: filters?.status,
                type: filters?.type,
                vector,
                match: { created_by: filters?.user },
            }
            : {}

        search.query = query

        search.search()
    }

    const loadMoreDocuments = () => {
        setIsReady(false)

        search.loadMore().then(() => {
            setIsReady(true)
        })
    }

    const handleOnClearFilters = () => {
        setClearFilters(clearFilters + 1)
    }

    useEffect(() => {
        if (!client || !project) {
            return
        }

        client.projects.retrieve(project.id).then((project) => {
            setConfig(project.configuration)
        })
    }, [project, client])

    useEffect(() => {
        if (!error) {
            return
        }

        toast.error("Error while searching documents", {
            description: error.message,
            duration: 5000,
        })
    }, [error])

    useEffect(() => {
        setTypes(transformFacetTypeResponses(facets.type, typeRegistry))
        setStatuses(transformFacetStatusResponses(facets.status))
        setUsers(transformFacetStatusResponses((facets as any).created_by))
    }, [facets])

    useEffect(() => {
        searchCollection()
    }, [client, cId])

    useEffect(() => {
        searchObjects()
    }, [collection])

    return (
        <div className="w-full h-full flex flex-col">
            <div className="w-full flex flex-row gap-2 items-center justify-between p-2 border-b">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={handleBackToSearch} className="hover:bg-gray-100 transition-colors duration-200">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                    <div className="text-2xl text-left font-semibold">{collection ? collection.name : "Loading data"}</div>
                </div>
            </div>
            <div className="flex h-[calc(100%-55px)] w-full">
                <FiltersPanel statuses={statuses} types={types} users={users} onFiltersChange={setFilters} onSearch={searchObjects} onClearFilters={clearFilters} />
                <div className="w-full h-full flex flex-col gap-4 p-4">
                    <div className="w-full flex flex-row gap-2 items-center justify-end">
                        <div className="flex gap-4 items-center">
                            {filters && Object.keys(filters).length > 0 && (
                                <Button onClick={handleOnClearFilters}>
                                    <FilterX />
                                    <span>Clear Filter{Object.keys(filters).length > 1 ? "s" : ""}</span>
                                    <span className="text-xs text-blue-500">({Object.keys(filters).length})</span>
                                </Button>
                            )}
                            <UploadDocumentsButton onUploadDone={() => searchObjects()}>
                                <Button>
                                    <Upload />
                                    <span>Upload Document(s)</span>
                                </Button>
                            </UploadDocumentsButton>
                            <ContentDispositionButton onUpdate={setIsGridView} />
                        </div>
                    </div>
                    <div className="flex-grow w-full overflow-auto px-2">
                        {
                            isGridView
                                ? <DocumentGrid documents={objects} loading={isSearching} hasMore={hasMore && isReady} loadMore={loadMoreDocuments} />
                                : <DocumentDataTable data={objects} loading={isSearching} hasMore={hasMore && isReady} loadMore={loadMoreDocuments} />
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}
